/**
 * Posisi — redesain: label penuh, angka IDR utuh (bisa turun baris),
 * tidak disingkat "1,2jt" / "3d".
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Position } from '../../lib/api';
import { fmt } from '../../lib/format';
import { buildMonths } from '../../lib/months';
import { copy } from './brand';
import { PullToRefresh } from './ui/PullToRefresh';
import { Skeleton } from './ui/Skeleton';

function investCoin(optType: string): 'USDT' | 'BTC' {
  return optType === 'PUT' ? 'USDT' : 'BTC';
}

function daysUntilSettle(p: Position): number | null {
  if (p.status !== 'active') return null;
  const ms = new Date(p.settle_date).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function statusLabel(p: Position): { label: string; level: 'ok' | 'wait' } {
  if (p.status === 'active') {
    const days = daysUntilSettle(p);
    if (days !== null && days <= 0) return { label: 'Siap settle', level: 'ok' };
    if (days === 1) return { label: '1 hari lagi', level: 'wait' };
    if (days !== null) return { label: `${days} hari lagi`, level: 'wait' };
    return { label: 'Aktif', level: 'wait' };
  }
  return { label: 'Sudah settle', level: 'ok' };
}

function Money({
  value,
  tone = 'default',
  prefix = '',
}: {
  value: number | null;
  tone?: 'default' | 'green' | 'amber';
  prefix?: string;
}) {
  if (value == null || !Number.isFinite(value)) {
    return <span className="tc-pos-money tc-pos-money--muted">—</span>;
  }
  return (
    <span className={`tc-pos-money tc-pos-money--${tone}`} title={fmt.idr(value)}>
      {prefix}
      {fmt.idr(value)}
    </span>
  );
}

export function MobilePositionsPage() {
  const months = useMemo(() => buildMonths(8), []);
  const [month, setMonth] = useState(months[0]?.value ?? '');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['positions', month],
    queryFn: () =>
      api.get<{ positions: Position[]; count: number }>(`/api/binance/positions?month=${month}`),
    refetchInterval: 30_000,
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      api.post<{ refreshed: number; total: number }>('/api/binance/positions/refresh'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['positions'] });
      if (navigator.vibrate) navigator.vibrate(12);
    },
  });

  const { data: btcPrice } = useQuery({
    queryKey: ['price', 'BTCUSDT'],
    queryFn: () => api.get<{ symbol: string; price: number }>('/api/binance/price?symbol=BTCUSDT'),
    refetchInterval: 30_000,
  });

  const { data: usdtIdr } = useQuery({
    queryKey: ['price', 'USDTIDR'],
    queryFn: () => api.get<{ symbol: string; price: number }>('/api/binance/price?symbol=USDTIDR'),
    refetchInterval: 60_000,
  });

  const positions = data?.positions || [];
  const active = positions.filter((p) => p.status === 'active');
  const usdtIdrRate = usdtIdr?.price ?? 0;
  const spotPrice = btcPrice?.price ?? 0;
  const hasRate = usdtIdrRate > 0;

  const modalOf = (p: Position) => {
    const inv = Number(p.invested) || 0;
    return investCoin(p.option_type) === 'USDT' ? inv * usdtIdrRate : inv * spotPrice * usdtIdrRate;
  };

  const estOf = (p: Position) => {
    const apr = Number(p.apr) || 0;
    const subs = new Date(p.subscribed_at);
    const settle = new Date(p.settle_date);
    const days = Math.max(1, Math.round((settle.getTime() - subs.getTime()) / 86_400_000));
    const inv = Number(p.invested) || 0;
    const est =
      p.option_type === 'PUT'
        ? inv * (apr / 100) * (days / 365)
        : spotPrice * (apr / 100) * (days / 365);
    return est * usdtIdrRate;
  };

  const totalModal = positions.reduce((s, p) => s + modalOf(p), 0);
  const totalEstProfit = positions.reduce((s, p) => s + estOf(p), 0);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['positions'] });
    await qc.invalidateQueries({ queryKey: ['price'] });
  };

  return (
    <div className="tc-mob-page tc-mob-page--ht tc-mob-page--eg">
      <PullToRefresh onRefresh={refresh}>
        <div className="tc-mob-page__header tc-mob-page__header--eg">
          <div className="tc-mob-page__title">{copy.positions.title}</div>
          <div className="tc-mob-page__sub">{copy.positions.sub}</div>
        </div>

        <label className="tc-pos-month">
          <span className="tc-pos-month__label">Bulan</span>
          <select
            className="tc-mob-select tc-pos-month__select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        {/* Summary — stacked full labels, full IDR */}
        <div className="tc-pos-summary">
          <div className="tc-pos-summary__row">
            <span className="tc-pos-summary__label">Total modal</span>
            <Money value={hasRate ? totalModal : null} tone="amber" />
          </div>
          <div className="tc-pos-summary__row">
            <span className="tc-pos-summary__label">Estimasi profit</span>
            <Money value={hasRate ? totalEstProfit : null} tone="green" />
          </div>
          <div className="tc-pos-summary__row tc-pos-summary__row--split">
            <div>
              <span className="tc-pos-summary__label">Posisi aktif</span>
              <div className="tc-pos-summary__num">{active.length}</div>
            </div>
            <div>
              <span className="tc-pos-summary__label">Semua posisi</span>
              <div className="tc-pos-summary__num">{positions.length}</div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="tc-mob-sync-btn"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? copy.positions.syncing : copy.positions.sync}
        </button>

        {syncMutation.isSuccess && syncMutation.data && (
          <div className="tc-mob-toast tc-mob-toast--ok">
            Berhasil sinkron {syncMutation.data.refreshed} posisi
          </div>
        )}

        {isLoading ? (
          <Skeleton rows={4} />
        ) : positions.length === 0 ? (
          <div className="tc-mob-empty">{copy.positions.empty}</div>
        ) : (
          <div className="tc-pos-list">
            {positions.map((p) => {
              const st = statusLabel(p);
              const modalIdr = modalOf(p);
              const estIdr = estOf(p);
              const apr = Number(p.apr) || 0;

              return (
                <article key={p.id} className="tc-pos-card">
                  <header className="tc-pos-card__head">
                    <div className="tc-pos-card__id">
                      <span
                        className={`tc-mob-badge tc-mob-badge--${
                          p.option_type === 'PUT' ? 'cyan' : 'amber'
                        }`}
                      >
                        {p.option_type === 'PUT' ? 'PUT' : 'CALL'}
                      </span>
                      <span className="tc-pos-card__symbol">{p.underlying}</span>
                    </div>
                    <span className={`tc-pos-card__status tc-pos-card__status--${st.level}`}>
                      {st.label}
                    </span>
                  </header>

                  <div className="tc-pos-card__strike-row">
                    <span className="tc-pos-card__k">Harga strike</span>
                    <span className="tc-pos-card__v">{fmt.number(p.strike_price, 0)}</span>
                  </div>

                  <div className="tc-pos-card__rows">
                    <div className="tc-pos-card__row">
                      <span className="tc-pos-card__k">Modal</span>
                      <Money value={hasRate ? modalIdr : null} tone="amber" />
                    </div>
                    <div className="tc-pos-card__row">
                      <span className="tc-pos-card__k">Imbal hasil (APR)</span>
                      <span className="tc-pos-card__v tc-pos-card__v--green">
                        {fmt.percent(apr, 2)}
                      </span>
                    </div>
                    <div className="tc-pos-card__row">
                      <span className="tc-pos-card__k">Estimasi profit</span>
                      <Money value={hasRate ? estIdr : null} tone="green" prefix="± " />
                    </div>
                    <div className="tc-pos-card__row">
                      <span className="tc-pos-card__k">Tanggal settle</span>
                      <span className="tc-pos-card__v">{fmt.dateDay(p.settle_date)}</span>
                    </div>
                    <div className="tc-pos-card__row">
                      <span className="tc-pos-card__k">Sisa waktu</span>
                      <span className="tc-pos-card__v">
                        {p.status === 'active'
                          ? (() => {
                              const d = daysUntilSettle(p);
                              if (d === null) return '—';
                              if (d <= 0) return 'Hari ini / lewat';
                              if (d === 1) return '1 hari lagi';
                              return `${d} hari lagi`;
                            })()
                          : 'Sudah settle'}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}
